// Promo fitting windows, published beside the bundle.
//
// The challenge fittings (Iron Shaft Challenge, Callaway Quantum Leap) are
// shown only inside a date window. Those dates used to live solely in
// fittingTypes.ts, which meant every extension CC asked for was a code
// change, a test run and a deploy — and on 2026-10-01 the Iron Shaft
// Challenge vanished for four days because the deploy had not happened.
//
// Salesforce cannot supply the dates: the promotion it attaches to each
// fitting is the store's generic CUSTOMFIT record (verified in production
// 2026-10-05), and retired products are left active. So the dates live in
// `promo-windows.json`, served from the same bucket as widget.js, cached for
// five minutes. Moving a window is one file upload (scripts/promo-windows.sh);
// the constants in fittingTypes.ts remain as the fallback when the file is
// unreachable, and the committed copy of the file is the source of truth.
//
// Rules:
//   - A fitting is matched by its canonical `name` in fittingTypes.ts.
//   - Only ISO 8601 date-times with an explicit offset are accepted. Anything
//     else — a bare date, a typo, a null — is ignored for that side, and the
//     fallback applies. A broken file can never hide or show a fitting that
//     the fallback would not.
//   - Loading is one fetch at boot. Failure of any kind is silent: the widget
//     renders on the fallback exactly as it did before this file existed.

export interface PromoWindow {
  startsAt?: string;
  endsAt?: string;
}

export type PromoWindows = Record<string, PromoWindow>;

export const PROMO_WINDOWS_FILE = 'promo-windows.json';

const ISO_WITH_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/;

let overrides: PromoWindows = {};

/** Accept only well-formed entries. Returns {} for anything unexpected. */
export function parsePromoWindows(raw: unknown): PromoWindows {
  if (!raw || typeof raw !== 'object') return {};
  const fittings = (raw as { fittings?: unknown }).fittings;
  if (!fittings || typeof fittings !== 'object') return {};

  const out: PromoWindows = {};
  for (const [name, value] of Object.entries(fittings as Record<string, unknown>)) {
    if (!name.trim() || !value || typeof value !== 'object') continue;
    const win: PromoWindow = {};
    for (const side of ['startsAt', 'endsAt'] as const) {
      const v = (value as Record<string, unknown>)[side];
      if (typeof v === 'string' && ISO_WITH_OFFSET.test(v.trim()) && !Number.isNaN(Date.parse(v))) {
        win[side] = v.trim();
      }
    }
    if (win.startsAt || win.endsAt) out[name] = win;
  }
  return out;
}

export function setPromoWindows(next: PromoWindows): void {
  overrides = next;
}

export function getPromoWindows(): PromoWindows {
  return overrides;
}

/** The published window for a fitting, if the file carries one. */
export function promoWindowFor(fittingName: string): PromoWindow | undefined {
  return overrides[fittingName];
}

/**
 * Where the file lives: next to the bundle, at the bucket root. Derived from
 * the bundle's own URL so prod and staging each read their own copy, and the
 * /v2/widget.js alias resolves to the same file. A host page can point
 * elsewhere with CC_WIDGET_CONFIG.promoWindowsUrl.
 */
export function promoWindowsUrl(scriptSrc: string | null | undefined): string | null {
  const cfg = (globalThis as { CC_WIDGET_CONFIG?: { promoWindowsUrl?: string } }).CC_WIDGET_CONFIG;
  if (cfg?.promoWindowsUrl) return cfg.promoWindowsUrl;
  if (!scriptSrc) return null;
  try {
    return new URL(`/${PROMO_WINDOWS_FILE}`, scriptSrc).toString();
  } catch {
    return null;
  }
}

/**
 * Fetch and apply the file. Never throws, never blocks: the fitting list is
 * not on screen until after Step 1, by which time this has long landed.
 */
export async function loadPromoWindows(url: string | null, fetchImpl: typeof fetch = fetch): Promise<PromoWindows> {
  if (!url) return overrides;
  try {
    const res = await fetchImpl(url, { credentials: 'omit' });
    if (!res.ok) return overrides;
    const parsed = parsePromoWindows(await res.json());
    setPromoWindows(parsed);
    return parsed;
  } catch {
    return overrides;
  }
}
